import { queryKeys } from '@/lib/query-keys';
import {
  apiError,
  catalogAdminClient,
  catalogCheckoutClient,
} from '@/lib/api/ts-rest';
import type {
  CreateAccessoryBody,
  CreateDeviceModelBody,
  CreateDeviceVariantBody,
  UpdateAccessoryBody,
  UpdateDeviceModelBody,
  UpdateDeviceVariantBody,
} from '@octave/contracts';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AddTravelEsimToCheckoutBody } from '@octave/contracts';
import { toast } from 'sonner';

const log = createLogger('useCatalogActions');

export const useCreateDeviceModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDeviceModelBody) => {
      const res = await catalogAdminClient.createDeviceModel({ body: data });
      if (res.status !== 201) throw new Error('Failed to create model');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModels() });
      toast.success('Modèle créé avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la création du modèle');
      log.error('Create device model error', error);
    },
  });
};

export const useUpdateDeviceModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateDeviceModelBody;
    }) => {
      const res = await catalogAdminClient.updateDeviceModel({
        params: { id },
        body: data,
      });
      if (res.status !== 200) throw new Error('Failed to update model');
      return res.body;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModels() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModel(id) });
      toast.success('Modèle mis à jour avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la mise à jour du modèle');
      log.error('Update device model error', error);
    },
  });
};

export const useDeleteDeviceModel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await catalogAdminClient.deleteDeviceModel({
        params: { id },
      });
      if (res.status !== 200) throw new Error('Failed to delete model');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModels() });
      toast.success('Modèle supprimé avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la suppression du modèle');
      log.error('Delete device model error', error);
    },
  });
};

export const useCreateDeviceVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDeviceVariantBody) => {
      const res = await catalogAdminClient.createDeviceVariant({ body: data });
      if (res.status !== 201) throw new Error('Failed to create variant');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceVariants() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModels() });
      toast.success('Déclinaison créée avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la création de la déclinaison');
      log.error('Create device variant error', error);
    },
  });
};

export const useUpdateDeviceVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateDeviceVariantBody;
    }) => {
      const res = await catalogAdminClient.updateDeviceVariant({
        params: { id },
        body: data,
      });
      if (res.status !== 200) throw new Error('Failed to update variant');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceVariants() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModels() });
      toast.success('Déclinaison mise à jour avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la mise à jour de la déclinaison');
      log.error('Update device variant error', error);
    },
  });
};

export const useDeleteDeviceVariant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await catalogAdminClient.deleteDeviceVariant({
        params: { id },
      });
      if (res.status !== 200) throw new Error('Failed to delete variant');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceVariants() });
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.deviceModels() });
      toast.success('Déclinaison supprimée avec succès');
    },
    onError: (error) => {
      toast.error('Erreur lors de la suppression de la déclinaison');
      log.error('Delete device variant error', error);
    },
  });
};

export const useCreateAccessory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAccessoryBody) => {
      const res = await catalogAdminClient.createAccessory({ body: data });
      if (res.status !== 201) throw new Error('Failed to create accessory');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.accessories() });
      toast.success('Accessoire créé avec succès');
    },
    onError: (error) => {
      toast.error("Erreur lors de la création de l'accessoire");
      log.error('Create accessory error', error);
    },
  });
};

export const useUpdateAccessory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateAccessoryBody;
    }) => {
      const res = await catalogAdminClient.updateAccessory({
        params: { id },
        body: data,
      });
      if (res.status !== 200) throw new Error('Failed to update accessory');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.accessories() });
      toast.success('Accessoire mis à jour avec succès');
    },
    onError: (error) => {
      toast.error("Erreur lors de la mise à jour de l'accessoire");
      log.error('Update accessory error', error);
    },
  });
};

export const useDeleteAccessory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await catalogAdminClient.deleteAccessory({ params: { id } });
      if (res.status !== 200) throw new Error('Failed to delete accessory');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.catalog.accessories() });
      toast.success('Accessoire supprimé avec succès');
    },
    onError: (error) => {
      toast.error("Erreur lors de la suppression de l'accessoire");
      log.error('Delete accessory error', error);
    },
  });
};

// Checkout hooks
export const useAddDeviceVariantToCheckout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      organizationId: string;
      deviceVariantId: string;
      quantity: number;
      siteId: string;
    }) => {
      const res = await catalogCheckoutClient.addDeviceVariantToCheckout({
        body: request,
      });
      if (res.status !== 201) throw new Error('Failed to add to checkout');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
      toast.success('Appareil ajouté au panier');
    },
    onError: (error) => {
      toast.error("Erreur lors de l'ajout au panier");
      log.error('Add device variant to checkout error', error);
    },
  });
};

export const useAddAccessoryToCheckout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      organizationId: string;
      accessoryId: string;
      quantity: number;
      siteId: string;
      deviceVariantId?: string;
      color?: string;
    }) => {
      const res = await catalogCheckoutClient.addAccessoryToCheckout({
        body: request,
      });
      if (res.status !== 201) throw new Error('Failed to add to checkout');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
      toast.success('Accessoire ajouté au panier');
    },
    onError: (error) => {
      toast.error("Erreur lors de l'ajout au panier");
      log.error('Add accessory to checkout error', error);
    },
  });
};

export const useAddTravelEsimToCheckout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AddTravelEsimToCheckoutBody) => {
      const res = await catalogCheckoutClient.addTravelEsimToCheckout({ body });
      if (res.status !== 201) throw apiError(res, 'Failed to add to checkout');
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.checkout.count() });
      toast.success('eSIM internationale ajoutée au panier');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Erreur lors de l'ajout au panier",
      );
      log.error('Add travel eSIM to checkout error', error);
    },
  });
};
